import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@mui/material/styles";
import ContentBox from "src/components/ContentBox/ContentBox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { Favorite, FavoriteBorder } from "@mui/icons-material";
import living_room from "src/media/living_room.jpg";
import kitchen from "src/media/kitchen.jpg";
import pool from "src/media/pool.jpg";
import template_image from "src/media/living_room.jpg";
import studentVerificationBadge from "src/media/pictures/student_verification.png";
import { Home } from "src/model_types/home.types";
import { useNavigate } from "react-router-dom";
import {
  useGetHomesQuery,
  useLikeHomeMutation,
} from "src/redux/service/home.service";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import theme from "src/styles/mainstyle";
import { getIsSignedIn } from "src/redux/reducers/user";
import MainButton from "src/components/buttons/mainbutton";
import { trackPostHogEvent } from "src/analytics/posthog";

export default function ImageCarousel() {
  const query = new URLSearchParams({
    limit: "4",
    sortByLikes: "true",
  }).toString();
  const { data: homes, isLoading: isHomesLoading } = useGetHomesQuery(
    "?" + query,
  );
  const navigate = useNavigate();
  const backupHomes: Partial<Home>[] = [
    {
      files: [{ downloadUrl: pool, fileName: "living_room" }],
      descriptions: { title: "Italian Villa" },
    },
    {
      files: [{ downloadUrl: kitchen, fileName: "kitchen" }],
      descriptions: { title: "Sao Paolo house" },
    },
    {
      files: [{ downloadUrl: living_room, fileName: "pool" }],
      descriptions: { title: "New York Studio" },
    },
  ];

  const showHomes: Partial<Home>[] = homes || backupHomes;

  const [likeHome, { isLoading: isLikingUpdating, isError: likeError }] =
    useLikeHomeMutation();
  if (isHomesLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </div>
    );
  }
  return (
    <ThemeProvider theme={theme}>
      <ContentBox title="Top homes right now">
        <Grid container spacing={4}>
          {showHomes?.map((home) => (
            <Grid item xs={12} md={3} key={String(home._id)}>
              <CarosouelImage
                home={home}
                caption={home?.descriptions?.title || ""}
                likeFunction={() => {
                      return likeHome({
                    homeId: String(home._id),
                    like: !home?.likedByUser,
                      }).unwrap();
                }}
                    analyticsSource="top_homes_carousel"
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Grid container justifyContent="flex-end">
              <MainButton
                onClick={() => navigate("/homes")}
                text={"Show more homes"}
              />
            </Grid>
          </Grid>
        </Grid>
      </ContentBox>
    </ThemeProvider>
  );
}

type CarosouelImageProps = {
  caption: string;
  home: Partial<Home>;
  likeFunction?: () => Promise<unknown>;
  location?: string;
  analyticsSource?: string;
};

export function CarosouelImage({
  caption,
  home,
  likeFunction,
  location,
  analyticsSource,
}: CarosouelImageProps) {
  // Inside your component
  const navigate = useNavigate();
  const isSignedIn = getIsSignedIn();
  console.log("Rendering CarosouelImage with home:", home);

  // Assuming you have a destination URL/path
  const destinationPath = "/homes/";

  const handleOpenHomeCard = (source: string) => {
    trackPostHogEvent("home_card_opened", {
      source,
    });
    navigate(destinationPath + String(home._id));
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            maxWidth: "90%",
            // boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.3)",
          }}
        >
          <img
            src={
              home.files?.length || 0 > 0
                ? home.files![0].downloadUrl
                : template_image
            }
            alt="First"
            style={{
              width: "100%",
              aspectRatio: "16 / 16",
              borderRadius: "12px",
              objectFit: "cover", // Ensure the image covers the specified height while maintaining aspect ratio
            }}
            onClick={() =>
              handleOpenHomeCard(analyticsSource || "home_card_image")
            }
          />
          {home.verifiedStudentHome && (
            <Tooltip title={"Verified student"}>
              <Box
                component="img"
                src={studentVerificationBadge}
                alt="Verified student home"
                sx={{
                  position: "absolute",
                  top: 6,
                  left: 6,
                  width: { xs: 24, sm: 28 },
                  height: { xs: 24, sm: 28 },
                  background: "white",
                  borderRadius: "50%",
                  padding: "2px",
                  boxShadow: 1,
                  zIndex: 2,
                }}
              />
            </Tooltip>
          )}
          {likeFunction && isSignedIn && (
            <IconButton
              onClick={async () => {
                try {
                  await likeFunction();
                  if (home.likedByUser) {
                    trackPostHogEvent("home_unfavorited", {
                      source: analyticsSource || "home_card",
                    });
                  } else {
                    trackPostHogEvent("home_favorited", {
                      source: analyticsSource || "home_card",
                    });
                  }
                } catch (error) {
                  return;
                }
              }}
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
              }}
            >
              {home.likedByUser ? (
                <Favorite sx={{ color: "red" }} />
              ) : (
                <FavoriteBorder sx={{ color: "red" }} />
              )}
            </IconButton>
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.9em",
            textDecoration: "none",
            cursor: "pointer",
            color: "inherit",
            textAlign: "center", // add this
            width: "90%", // match the image width
            "&:hover": {
              textDecoration: "underline",
              cursor: "pointer",
            },
          }}
          onClick={() =>
            handleOpenHomeCard(analyticsSource || "home_card_caption")
          }
        >
          {caption}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.75em",
            textDecoration: "none",
            cursor: "pointer",
            color: "inherit",
            fontStyle: "italic",
            textAlign: "center", // add this
            width: "90%", // match the image width
            "&:hover": {
              textDecoration: "underline",
              cursor: "pointer",
            },
          }}
          onClick={() =>
            handleOpenHomeCard(analyticsSource || "home_card_location")
          }
        >
          {location}
        </Typography>
      </Box>
    </ThemeProvider>
  );
}
